import { FC, Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Badge, Button, LoadingLogo, Typography } from 'components';
import { ContentSourceEnum, QualityEnum, TranslationRequestOrderDetails } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { WarningIcon } from 'lib/svgs/Icons';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useTranslationRequestCreate } from 'providers/TranslationRequests/useTranslationRequestCreate';
import { notifyException } from 'services/appsignal';
import { roundPriceUtil } from 'utils/roundPriceUtil';
import { ITrParams } from './Sidebar';
import { getParamsCreateTr, updateOrderDetails, updateSidebarFlow } from './_actions';
import { SIDEBAR_FLOWS } from './_consts';

interface IOrderDetails {
  ordering: boolean;
  quality: string;
  sourceLanguageCode: string;
  targetLanguageCodes: string[];
  wordCount: number;
  wouldNeedDetails: boolean;
  orderDetails: TranslationRequestOrderDetails;
  updateSidebarFlow: (flow: string) => void;
  updateOrderDetails: (orderDetails: unknown) => void;
  getParamsCreateTr: () => any;
}

const OrderDetails: FC<IOrderDetails> = ({
  ordering,
  quality,
  sourceLanguageCode,
  targetLanguageCodes,
  wordCount,
  wouldNeedDetails,
  orderDetails: { details, detailsTotal },
  getParamsCreateTr,
  updateSidebarFlow,
  updateOrderDetails
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const [isQuote, setIsQuote] = useState(false);
  const { mutateAsync: translationRequestCreate, isPending } = useTranslationRequestCreate();

  const router = useRouter();

  const [showLoading, setShowLoading] = useState(false);

  const trCreating = async (quote: boolean) => {
    try {
      setIsQuote(quote);
      const params: ITrParams = await getParamsCreateTr();

      await translationRequestCreate({
        contentSource: params.isAFile ? ContentSourceEnum.UploadedFileId : ContentSourceEnum.Copy,
        quality: params.quality as QualityEnum,
        copy: params.copy,
        autoClientReview: params.autoClientReview,
        purchaseOrderCode: params.purchaseOrderCode,
        sendCompletionEmail: params.sendCompletionEmail,
        sourceLanguageCode: params.sourceLanguageCode,
        targetLanguageCodes: params.targetLanguageCodes,
        name: params.name,
        proposedClientDeadline: params.proposedClientDeadline,
        styleGuideReferenceId: params.styleGuideReferenceId,
        generalEditorId: params.generalEditorId,
        plainText: params.plainText,
        editorPerLanguage: params.editorPerLanguage,
        uploadedFileIds: params.uploadedFileIds,
        assignedGlossariesIds: params.assignedGlossariesIds,
        callbackUrl: params.callbackUrl,
        contextUrl: params.contextUrl,
        comment: params.comment,
        excluded: params.excluded,
        editorIdsPerLanguage: params.editorIdsPerLanguage,
        keyValueData: params.keyValueData,
        integration: params.integration,
        included: params.included,
        workflowId: params.workflowId,
        quote
      });

      quote
        ? toaster.success(t('messages.success.tr_creation_quote'))
        : toaster.success(t('messages.success.tr_creation_order'));

      router.push('/translation/translation-requests');
    } catch (error) {
      notifyException(error);

      if (quote) return toaster.error(t('messages.error.tr_creation_quote'));

      toaster.error(t('messages.error.tr_creation_order'));
      setIsQuote(false);
    }
  };

  const orderContent = async () => {
    if (details) {
      const totalCost = details?.reduce((acc, { cost }) => acc + (cost ?? 0), 0);

      if (wouldNeedDetails && totalCost > 0)
        return updateSidebarFlow(SIDEBAR_FLOWS.PAYMENT_DETAILS);

      await trCreating(false);
    }
  };

  const handleQuote = async () => trCreating(true);
  const handleBack = async () => {
    updateOrderDetails({});
    updateSidebarFlow(SIDEBAR_FLOWS.INPUTS);
  };

  useEffect(() => {
    if (isPending) setShowLoading(true);

    setTimeout(() => {
      setShowLoading(false);
    }, 3000);
  }, [isPending]);

  useEffect(() => {
    if (window !== undefined)
      window.scrollTo({
        top: 0,
        behavior: 'instant'
      });
  }, []);

  return (
    <div className="ordering_form__sidebar">
      {!details && (
        <div className={'mt-10 h-[400px] w-full'}>
          <LoadingLogo size="x-small" />
        </div>
      )}
      {details && (
        <div>
          <h2 className="ordering_form__details_total ordering_form__spread_halfs">
            <Typography variant="bold1" className="text-tertiary-100">
              {t('ordering_form.words_to_translate')}
            </Typography>
            <Typography variant="bold1" className="mt-0 text-tertiary-100 ">
              {new Intl.NumberFormat(locale, {
                notation: 'standard'
              }).format(wordCount)}
            </Typography>
          </h2>

          {targetLanguageCodes?.map((tlc) => {
            const source = t(`languages.${sourceLanguageCode}` as any);
            const target = t(`languages.${tlc}` as any);

            const detailsPerLanguage = details?.find((detail) => detail.languageCode === tlc);

            return (
              <Fragment key={tlc}>
                <div className="flex justify-between">
                  <Typography variant="medium3" className="text-quaternary-100 ">
                    {t('ordering_form.language_pair_source_target', { source, target })}
                  </Typography>
                  {detailsPerLanguage?.duplicateContent && (
                    <div className="ordering_form__spread_halfs">
                      <Badge
                        icon={<WarningIcon className="scale-[0.85] !text-white" />}
                        className="underline-none relative h-6 !bg-status-yellow !py-0"
                        classNameTypography="text-white !text-xs !font-regular "
                      >
                        {t('ordering_form.duplicated_content')}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="ordering_form__spread_halfs">
                  <Typography variant="regular4" className="text-tertiary-200 ">
                    {t('ordering_form.words_to_order')}
                  </Typography>
                  <Typography variant="regular4" className="text-tertiary-200 ">
                    {detailsPerLanguage?.words ?? 0}
                  </Typography>
                </div>
                <div className="ordering_form__spread_halfs">
                  <Typography variant="regular4" className="text-tertiary-200 ">
                    {t('ordering_form.tm_savings')}
                  </Typography>
                  <Typography variant="regular4" className="text-tertiary-200 ">
                    {Number(detailsPerLanguage?.savings ?? 0).toFixed(2) + ' %'}
                  </Typography>
                </div>
                <div className="ordering_form__spread_halfs">
                  <Typography variant="regular4" className="capitalize text-tertiary-200 ">
                    {t('ordering_form.quality_price', { quality: quality })}
                  </Typography>
                  <Typography variant="regular4" className="text-tertiary-200 ">
                    {detailsPerLanguage?.pricePerWord ?? '0.00'}
                  </Typography>
                </div>
              </Fragment>
            );
          })}

          <div className="ordering_form__spread_halfs mt-12 border-t-1 border-dashed border-tertiary-300 pt-4">
            <Typography variant="medium1" className="text-tertiary-100">
              {t('ordering_form.total')}
            </Typography>
            <div style={{ textAlign: 'right' }}>
              <Typography variant="medium1" className="text-tertiary-100">
                {roundPriceUtil(detailsTotal || '')}
              </Typography>
              <br />
              <Typography variant="regular5" className="text-tertiary-200 ">
                {t('ordering_form.vat_not_included')}
              </Typography>
            </div>
          </div>
          {!ordering && (
            <div className="mt-16 flex flex-col justify-center gap-4">
              <Button
                variant="contained"
                disabled={ordering || !details}
                onClick={orderContent}
                loading={showLoading && !isQuote}
              >
                {t('ordering_form.order_this_translation')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={ordering || !details}
                onClick={handleQuote}
                loading={showLoading && isQuote}
              >
                {t('ordering_form.quote_this_translation_request')}
              </Button>
              <Button
                label={t('ordering_form.back_to_order_config')}
                onClick={handleBack}
                size="medium"
                variant="text"
                className="mt-4"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default connect(
  ({
    orderDetails,
    ordering,
    wordCount,
    wouldNeedDetails,
    inputs: { targetLanguageCodes, sourceLanguageCode, quality }
  }) => ({
    orderDetails,
    ordering,
    quality,
    sourceLanguageCode,
    targetLanguageCodes,
    wordCount,
    wouldNeedDetails
  }),
  {
    updateSidebarFlow,
    updateOrderDetails,
    getParamsCreateTr
  }
)(OrderDetails);
